import React, {useState, useEffect, useRef} from 'react';
import {useNavigate, useLocation} from 'react-router';
import './App.css';

import getMenuItems from './Helpers/MainMenuItems';
import Navbar from "./Components/Navbar";
import ReleaseNotes from "./Components/ReleaseNotes";
import Stratagems from "./Components/Stratagems";
import PointsData from "./Data/pointsData";
import UnitDisplay from "./Components/UnitDisplay";
import ArmyRules from "./Components/ArmyRules";
import {Helmet} from "react-helmet";
import UnitMenu from "./Components/Menu";

function App() {
    const menuRef = useRef(null);
    const [selectedData, setSelectedData] = useState(null);
    const [selectedMenu, setSelectedMenu] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [totalPoints, setTotalPoints] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');

    const [selectedDropdownOption, setSelectedDropdownOption] = useState('None');
    const [selectedCheckboxes, setSelectedCheckboxes] = useState([]);
    const [showCheckedItemsOnly, setShowCheckedItemsOnly] = useState(false);
    const [selectedItems, setSelectedItems] = useState({});
    const [submenuItemValues, setSubmenuItemValues] = useState({});
    const [activePage, setActivePage] = useState('releaseNotes');

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const army = params.get('army');
        const unit = params.get('unit');
        const page = params.get('page');

        if (army) {
            setSelectedMenu(army);
            setSelectedDropdownOption(army);
        }

        if (unit) {
            const menuItems = getMenuItems();
            const selectedArmy = menuItems.find(item => item.menu === army);
            if (selectedArmy) {
                const selectedUnit = selectedArmy.datasheets.find(data => data.NAME === unit);
                if (selectedUnit) {
                    setSelectedData(selectedUnit);
                }
            }
        }

        if (page)
            setActivePage(page);
        
    }, [location.search]);

    useEffect(() => {
        const params = new URLSearchParams();
        if (selectedMenu) {
            params.set('army', selectedMenu);
        }
        if (selectedData) {
            params.set('unit', selectedData.NAME);
        }
        if (activePage !== 'releaseNotes') {
            params.set('page', activePage);
        }
        navigate({search: params.toString()});
    }, [selectedMenu, selectedData, activePage, navigate]);

    const handleButtonClick = (page) => {
        setActivePage(page);
        setSelectedData(null);
    };

    const handleDataItemClick = (data, menu) => {
        setSelectedData(data);
        setSelectedMenu(menu);
        setMenuOpen(false);
        setActivePage('unitDisplay');
    };

    const handleDocumentClick = (event) => {
        if (menuOpen && !menuRef.current.contains(event.target)) {
            setMenuOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('click', handleDocumentClick);
        return () => {
            document.removeEventListener('click', handleDocumentClick);
        };
    }, [menuOpen]);

    const handleSubMenuClick = (menu) => {
        if (selectedMenu === menu && menuOpen) {
            setSelectedMenu(menu);
            setMenuOpen(false);
        } else {
            setSelectedMenu(menu);
            setMenuOpen(true);
        }
    };

    const handleToggleMenu = (event) => {
        event.stopPropagation(); // Prevent click event propagation
        setMenuOpen(!menuOpen);
    };

    const handleSearchInputChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const filterMenuItems = () => {
        let filteredItems = getMenuItems();

        if (selectedDropdownOption !== "None") {
            filteredItems = filteredItems.filter(
                ({menu}) => menu.toLowerCase() === selectedDropdownOption.toLowerCase()
            );
        }

        if (showCheckedItemsOnly) {
            filteredItems = filteredItems.map(({menu, datasheets}) => ({
                menu,
                datasheets: datasheets.filter((subItem) =>
                    selectedCheckboxes.includes(subItem)
                ),
            }));
        }

        filteredItems.forEach(({datasheets}) => {
            datasheets.sort((a, b) => a.NAME.localeCompare(b.NAME));
        });

        // Expand all sub-items if "Show Checked Items Only" is checked
        if (showCheckedItemsOnly) {
            filteredItems.forEach((item) => {
                item.expanded = true;
            });
        }

        return filteredItems.filter(({datasheets}) => datasheets.length > 0);
    };

    const handleAddItemClick = (event, submenuItem) => {
        event.stopPropagation(); // Prevent click event propagation
        setSelectedCheckboxes((prevCheckboxes) => [...prevCheckboxes, submenuItem]);

        const itemName = submenuItem.NAME;

        const selectedItemData = PointsData.find(
            (data) => data.name.toLowerCase() === itemName.toLowerCase()
        );

        if (!selectedItemData)
            return;

        const amountData = PointsData.find(
            (data) => data.name.toLowerCase() === itemName.toLowerCase()
        ).amount;

        const pointsData = PointsData.find(
            (data) => data.name.toLowerCase() === itemName.toLowerCase()
        ).points;

        const amountArray = [];
        const pointsArray = [];

        let repeatCount = submenuItem.KEYWORDS.some(keyword =>
            keyword.toLowerCase() === "epic hero")
            ? 1
            : submenuItem.KEYWORDS.some(keyword =>
                keyword.toLowerCase() === "battleline"
                || keyword.toLowerCase() === "dedicated transport"
            )
                ? 6
                : 3;

        for (let i = 0; i < repeatCount; i++) {
            let lastElement = amountData[amountData.length - 1] * i;
            for (let x = 0; x < amountData.length; x++) {
                amountArray.push(amountData[x] + lastElement);
            }
        }

        for (let i = 0; i < repeatCount; i++) {
            let lastElement = pointsData[pointsData.length - 1] * i;
            for (let x = 0; x < pointsData.length; x++) {
                pointsArray.push(pointsData[x] + lastElement);
            }
        }

        const numClicks = selectedItems[submenuItem.NAME] || 0;
        const currentIndex = amountArray.indexOf(numClicks);
        const nextIndex = (currentIndex + 1) % amountArray.length;

        if (nextIndex === 0 && currentIndex === amountArray.length - 1) {
            const newSelectedItems = {
                ...selectedItems,
                [submenuItem.NAME]: numClicks,
            };
            setTotalPoints(totalPoints);
            setSelectedItems(newSelectedItems);

            return {value: numClicks, points: pointsArray[currentIndex]};
        }

        const nextValue = amountArray[nextIndex];
        const nextPoints = pointsArray[nextIndex];
        const currentPoints = pointsArray[currentIndex];

        const newSelectedItems = {
            ...selectedItems,
            [submenuItem.NAME]: nextValue,
        };

        setSelectedItems(newSelectedItems);
        setTotalPoints(numClicks === 0 ? totalPoints + nextPoints : totalPoints + nextPoints - currentPoints);
        return {value: nextValue, points: nextPoints};
    };

    const handleRemoveItemClick = (event, submenuItem) => {
        event.stopPropagation(); // Prevent click event propagation
        setSelectedCheckboxes((prevCheckboxes) => [...prevCheckboxes, submenuItem]);

        const itemName = submenuItem.NAME;
        const amountData = PointsData.find(
            (data) => data.name.toLowerCase() === itemName.toLowerCase()
        ).amount;

        const pointsData = PointsData.find(
            (data) => data.name.toLowerCase() === itemName.toLowerCase()
        ).points;

        const amountArray = [];
        const pointsArray = [];

        let repeatCount = submenuItem.KEYWORDS.includes("Epic Hero")
            ? 1
            : submenuItem.KEYWORDS.includes("Battleline")
                ? 6
                : 3;

        for (let i = 0; i < repeatCount; i++) {
            let lastElement = amountData[amountData.length - 1] * i;
            for (let x = 0; x < amountData.length; x++) {
                amountArray.push(amountData[x] + lastElement);
            }
        }

        for (let i = 0; i < repeatCount; i++) {
            let lastElement = pointsData[pointsData.length - 1] * i;
            for (let x = 0; x < pointsData.length; x++) {
                pointsArray.push(pointsData[x] + lastElement);
            }
        }

        const numClicks = selectedItems[submenuItem.NAME] || 0;
        const currentIndex = amountArray.indexOf(numClicks);
        const prevValue = amountArray[currentIndex - 1];
        let currentPoints = pointsArray[currentIndex];
        let prevPoints = pointsArray[currentIndex - 1];

        const newSelectedItems = {
            ...selectedItems,
            [submenuItem.NAME]: prevValue,
        };

        const newItemCount = (selectedItems[submenuItem.NAME] || 0) - 1;
        if (newItemCount <= 0) {
            delete newSelectedItems[submenuItem.NAME];
            setSelectedCheckboxes((prevCheckboxes) =>
                prevCheckboxes.filter((item) => item !== submenuItem)
            );
        }

        setSelectedItems(newSelectedItems);

        if (isNaN(currentPoints))
            currentPoints = 0;

        if (isNaN(prevPoints))
            prevPoints = 0;

        let total = parseInt(totalPoints);
        if (isNaN(total))
            total = 0;

        setTotalPoints(total - currentPoints + prevPoints);
        return {value: prevValue, points: prevPoints};
    };

    const handleDropdownOptionChange = (event) => {
        const selectedOption = event.target.value;
        setSelectedDropdownOption(selectedOption);

        if (selectedOption === 'None') {
            setSelectedMenu(null);
            setSearchQuery(''); // Reset search query
        } else {
            setSelectedMenu(selectedOption);
            setSearchQuery(''); // Clear search query to show all submenu items
        }
    };

    const armySelector = <select
        className="w-full  px-2  py-1  rounded-sm  bg-white  text-slate-800  text-base  appearance-none"
        value={selectedDropdownOption}
        onChange={handleDropdownOptionChange}
    >
        <option value="None">-- Choose army --</option>
        {getMenuItems().map(({menu}) => (
            <option key={menu} value={menu}>
                {menu}
            </option>
        ))}
    </select>
    const handleClearData = () => {
        setSelectedData(null);
        setSelectedMenu(null);
        setMenuOpen(true);
        setTotalPoints(0);
        setSearchQuery('');
        setSelectedDropdownOption('None');
        setSelectedCheckboxes([]);
        setSelectedItems({});
        setSubmenuItemValues({});
        localStorage.clear();
    };

    return (
        <div className="app-container  bg-slate-50  text-slate-600  text-sm  md:text-base">
            <Helmet>
                <meta charSet="utf-8"/>
                <title>Temple of All Knowledge</title>
                <link rel="canonical" href="https://warhammerunits.netlify.app/"/>
                <link rel="icon" type="image/png" href="./favicon.ico" sizes="16x16"/>
                <meta name="description" content="Same old Warhammer, but slightly easier to read"/>
            </Helmet>
            <div>
                <Navbar handleButtonClick={handleButtonClick} handleToggleMenu={handleToggleMenu}/>
                <div className="p-5">
                    {activePage === 'releaseNotes' && <ReleaseNotes/>}
                    {activePage === 'stratagems' && (
                        <Stratagems
                            stratagems={
                                (() => {
                                    const menuItems = getMenuItems();
                                    const selectedItem = menuItems.find(item => item.menu === selectedMenu);
                                    return selectedItem ? selectedItem.stratagems : [];
                                })()
                            }
                        />
                    )}
                    {activePage === 'armyRules' && (
                        <ArmyRules
                            armyRules={
                                (() => {
                                    const menuItems = getMenuItems();
                                    const selectedItem = menuItems.find(item => item.menu === selectedMenu);
                                    return selectedItem ? selectedItem.armyRules : [];
                                })()
                            }
                        />
                    )}
                    {activePage === 'unitDisplay' && selectedData && (
                        <UnitDisplay selectedData={selectedData} handleDataItemClick={handleDataItemClick}
                                     selectedMenu={selectedMenu}/>
                    )}
                </div>
            </div>

            {UnitMenu(menuOpen, menuRef, armySelector, handleClearData, searchQuery, handleSearchInputChange, showCheckedItemsOnly, setShowCheckedItemsOnly, totalPoints, filterMenuItems, handleSubMenuClick, selectedMenu, selectedItems, submenuItemValues, handleAddItemClick, setSubmenuItemValues, handleRemoveItemClick, handleDataItemClick)}
        </div>
    );
}

export default App;